/**
 * Exécution des passages — terrain PRO (workflow passage → preuves → validation → facturation).
 * Ancienne liste « interventions libres » et planification POST /chef/interventions : retirées (voir audit doc).
 */
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  ClipboardList,
  ExternalLink,
  FileSpreadsheet,
  Flag,
  Loader2,
  MapPin,
  Navigation,
  Play,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { api } from "../../../lib/axios";
import { useAuth, type UserRole } from "../../../context/AuthContext";
import { ClientDemandesAdminPanel } from "../../../components/planificateur/ClientDemandesAdminPanel";
import type { PassageApiItem, TourneeApiItem } from "../../planification-logistique/planificateur/planningTypes";
import {
  clientLabel,
  effectiveStatutUpper,
  localIsoDate,
  passageStatutLecture,
  passageStatutLectureBadgeClass,
} from "../../planification-logistique/planificateur/planningDashboardUtils";
import {
  computeExecutionKpis,
  filterPassagesBySearch,
  gpsBadgeKind,
  passageExecutionStartUi,
  partitionExecutionPassages,
  photoFraction,
  userCanValidate,
  passageValidationColumnLabel,
  validationQualiteSubtitle,
  validationUi,
  type ValidationUi,
} from "./passageExecutionUtils";
import { passageExecutionOperationalColumnLabel } from "../../../features/passages/utils/passageOperationalLabels";
import { PassageExecutionDrawer } from "./PassageExecutionDrawer";

const ADMIN_PLAN = "/admin/planificateur";
const ADMIN_PASSAGES = "/admin/planificateur/passages";
const ADMIN_TOURNEES = "/admin/planificateur/tournees";

function KpiTile(props: {
  label: string;
  value: string | number;
  hint?: string;
  accent?: "emerald" | "amber" | "rose" | "sky" | "slate" | "violet";
}): React.JSX.Element {
  const ring =
    props.accent === "amber"
      ? "ring-amber-500/20"
      : props.accent === "rose"
        ? "ring-rose-500/20"
        : props.accent === "sky"
          ? "ring-sky-500/20"
          : props.accent === "slate"
            ? "ring-slate-500/25"
            : props.accent === "violet"
              ? "ring-violet-500/20"
              : "ring-emerald-500/20";
  return (
    <article className={`rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ${ring}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{props.label}</p>
      <p className="mt-2 text-2xl font-bold tabular-nums tracking-tight text-white">{props.value}</p>
      {props.hint ? <p className="mt-1 text-xs text-slate-500">{props.hint}</p> : null}
    </article>
  );
}

function coordsFromBrowser(): Promise<{ latitude: number; longitude: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
      },
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 }
    );
  });
}

function validationBadgeClasses(v: ValidationUi): string {
  if (v === "valide") return "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30";
  if (v === "rejete") return "bg-rose-500/15 text-rose-200 ring-rose-500/25";
  if (v === "clos_anomalies") return "bg-amber-500/15 text-amber-200 ring-amber-500/35";
  if (v === "en_attente") return "bg-amber-500/15 text-amber-200 ring-amber-500/25";
  return "bg-slate-700/50 text-slate-400 ring-slate-600/40";
}

function gpsBadgeClasses(kind: ReturnType<typeof gpsBadgeKind>): string {
  if (kind === "ok") return "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30";
  if (kind === "suspect") return "bg-rose-500/15 text-rose-200 ring-rose-500/30";
  return "bg-slate-700/50 text-slate-400 ring-slate-600/40";
}

function gpsBadgeText(kind: ReturnType<typeof gpsBadgeKind>): string {
  if (kind === "ok") return "OK";
  if (kind === "suspect") return "Suspect";
  return "Non relevé";
}

const InterventionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const todayIso = useMemo(() => localIsoDate(new Date()), []);

  const [loading, setLoading] = useState(true);
  const [passages, setPassages] = useState<PassageApiItem[]>([]);
  const [tournees, setTournees] = useState<TourneeApiItem[]>([]);
  const [search, setSearch] = useState("");
  /** Faux par défaut : masque les créneaux futurs (`includeFuture=false`). */
  const [includeUpcoming, setIncludeUpcoming] = useState(false);
  const [includeBlocked, setIncludeBlocked] = useState(false);
  const [apiKpis, setApiKpis] = useState<{
    passagesActionnables: number;
    passagesOverdue: number;
    passagesToday: number;
    passagesInProgress: number;
    passagesPendingValidation: number;
    passagesValidated: number;
    passagesFacturables: number;
  } | null>(null);

  const [drawerPassage, setDrawerPassage] = useState<PassageApiItem | null>(null);

  type PreuveModal = { passageId: number; type: "AVANT" | "APRES" };
  const [preuveModal, setPreuveModal] = useState<PreuveModal | null>(null);
  const [preuveUrl, setPreuveUrl] = useState("");
  const [preuveLat, setPreuveLat] = useState("");
  const [preuveLng, setPreuveLng] = useState("");

  const [finishId, setFinishId] = useState<number | null>(null);
  const [finishLat, setFinishLat] = useState("");
  const [finishLng, setFinishLng] = useState("");
  const [finishComment, setFinishComment] = useState("");

  const [validateId, setValidateId] = useState<number | null>(null);
  const [validateComment, setValidateComment] = useState("");

  const [rejectId, setRejectId] = useState<number | null>(null);
  const [rejectMotif, setRejectMotif] = useState("");
  const [rejectStatut, setRejectStatut] = useState<"NON_EFFECTUE" | "REPORTE">("NON_EFFECTUE");

  const [busyId, setBusyId] = useState<number | null>(null);

  const tourneeById = useMemo(() => {
    const m = new Map<number, TourneeApiItem>();
    for (const t of tournees) m.set(t.id, t);
    return m;
  }, [tournees]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const pRes = await api.get<{
        items?: PassageApiItem[];
        tournees?: TourneeApiItem[];
        kpis?: {
          passagesActionnables: number;
          passagesOverdue: number;
          passagesToday: number;
          passagesInProgress: number;
          passagesPendingValidation: number;
          passagesValidated: number;
          passagesFacturables: number;
        };
      }>("/execution-passages", {
        params: {
          includeFuture: includeUpcoming ? "true" : "false",
          includeUpcoming: includeUpcoming ? "true" : "false",
          includeBlocked: includeBlocked ? "true" : "false",
        },
      });
      setPassages(Array.isArray(pRes.data?.items) ? pRes.data.items : []);
      setTournees(Array.isArray(pRes.data?.tournees) ? pRes.data.tournees : []);
      setApiKpis(pRes.data?.kpis ?? null);
    } catch {
      toast.error("Impossible de charger les passages à exécuter.");
      setPassages([]);
      setTournees([]);
      setApiKpis(null);
    } finally {
      setLoading(false);
    }
  }, [includeUpcoming, includeBlocked]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onExternal = (): void => {
      void refresh();
    };
    window.addEventListener("adminExecutionPassagesRefresh", onExternal);
    return () => window.removeEventListener("adminExecutionPassagesRefresh", onExternal);
  }, [refresh]);

  const filtered = useMemo(() => filterPassagesBySearch(passages, search), [passages, search]);
  const executionSections = useMemo(() => partitionExecutionPassages(filtered), [filtered]);
  const kpisLocal = useMemo(() => computeExecutionKpis(passages), [passages]);
  const kpis = useMemo(() => {
    if (apiKpis) {
      return {
        passagesAExecuter: apiKpis.passagesOverdue + apiKpis.passagesToday,
        enRetard: apiKpis.passagesOverdue,
        aujourdhui: apiKpis.passagesToday,
        enCours: apiKpis.passagesInProgress,
        enAttenteValidation: apiKpis.passagesPendingValidation,
        valides: apiKpis.passagesValidated,
        suspectsGps: kpisLocal.suspectsGps,
        facturables: apiKpis.passagesFacturables,
        actionnables: apiKpis.passagesActionnables,
      };
    }
    return { ...kpisLocal, actionnables: kpisLocal.passagesAExecuter };
  }, [apiKpis, kpisLocal]);

  const canVal = userCanValidate(user?.role as UserRole | undefined);

  const pickTournee = useCallback(
    (p: PassageApiItem): TourneeApiItem | null => {
      if (p.tourneeId == null) return null;
      return tourneeById.get(p.tourneeId) ?? null;
    },
    [tourneeById]
  );

  const openDrawer = (p: PassageApiItem): void => {
    setDrawerPassage(p);
  };

  const patchLocalPassage = (updated: PassageApiItem): void => {
    setPassages((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    setDrawerPassage((d) => (d && d.id === updated.id ? { ...d, ...updated } : d));
  };

  const runStart = async (id: number): Promise<void> => {
    setBusyId(id);
    try {
      const c = await coordsFromBrowser();
      const res = await api.post<{ passage?: PassageApiItem }>(
        `/passages/${id}/start`,
        {
          ...(c ? { latitude: c.latitude, longitude: c.longitude } : {}),
        },
        { skipGlobalErrorToast: true }
      );
      if (res.data?.passage) patchLocalPassage(res.data.passage as PassageApiItem);
      toast.success("Passage démarré (heure serveur enregistrée).");
      await refresh();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string; message?: string } } };
      const msg =
        err.response?.data?.message ??
        err.response?.data?.error ??
        "Démarrage impossible.";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const submitPreuve = async (): Promise<void> => {
    if (!preuveModal) return;
    const url = preuveUrl.trim();
    if (!url) {
      toast.warning("Indiquez l’URL de la photo (après dépôt fichier).");
      return;
    }
    setBusyId(preuveModal.passageId);
    try {
      const lat = preuveLat.trim() ? Number(preuveLat) : undefined;
      const lng = preuveLng.trim() ? Number(preuveLng) : undefined;
      const res = await api.post<{ passage?: PassageApiItem }>(
        `/passages/${preuveModal.passageId}/preuves`,
        {
          type: preuveModal.type,
          photoUrl: url,
          ...(lat !== undefined && !Number.isNaN(lat) ? { latitude: lat } : {}),
          ...(lng !== undefined && !Number.isNaN(lng) ? { longitude: lng } : {}),
        },
        { skipGlobalErrorToast: true }
      );
      if (res.data?.passage) patchLocalPassage(res.data.passage as PassageApiItem);
      toast.success("Preuve enregistrée.");
      setPreuveModal(null);
      setPreuveUrl("");
      setPreuveLat("");
      setPreuveLng("");
      await refresh();
    } catch {
      toast.error("Enregistrement preuve impossible.");
    } finally {
      setBusyId(null);
    }
  };

  const runFinish = async (): Promise<void> => {
    if (finishId == null) return;
    setBusyId(finishId);
    try {
      const lat = finishLat.trim() ? Number(finishLat) : undefined;
      const lng = finishLng.trim() ? Number(finishLng) : undefined;
      const res = await api.post<{ passage?: PassageApiItem }>(
        `/passages/${finishId}/finish`,
        {
          ...(lat !== undefined && !Number.isNaN(lat) ? { latitude: lat } : {}),
          ...(lng !== undefined && !Number.isNaN(lng) ? { longitude: lng } : {}),
          ...(finishComment.trim() ? { commentaire: finishComment.trim() } : {}),
        },
        { skipGlobalErrorToast: true }
      );
      if (res.data?.passage) patchLocalPassage(res.data.passage as PassageApiItem);
      toast.success("Passage terminé — en attente de validation.");
      setFinishId(null);
      setFinishLat("");
      setFinishLng("");
      setFinishComment("");
      await refresh();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string; message?: string } } };
      const msg =
        err.response?.data?.message ??
        err.response?.data?.error ??
        "Clôture terrain impossible.";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const runValidate = async (): Promise<void> => {
    if (validateId == null) return;
    setBusyId(validateId);
    try {
      const res = await api.post<{ passage?: PassageApiItem }>(
        `/passages/${validateId}/validate`,
        { ...(validateComment.trim() ? { commentaireValidation: validateComment.trim() } : {}) },
        { skipGlobalErrorToast: true }
      );
      if (res.data?.passage) patchLocalPassage(res.data.passage as PassageApiItem);
      toast.success("Validation enregistrée.");
      setValidateId(null);
      setValidateComment("");
      await refresh();
    } catch {
      toast.error("Validation impossible.");
    } finally {
      setBusyId(null);
    }
  };

  const runReject = async (): Promise<void> => {
    if (rejectId == null) return;
    if (rejectMotif.trim().length < 5) {
      toast.warning("Motif (min. 5 caractères) requis.");
      return;
    }
    setBusyId(rejectId);
    try {
      const res = await api.post<{ passage?: PassageApiItem }>(
        `/passages/${rejectId}/reject`,
        {
          motif: rejectMotif.trim(),
          statut: rejectStatut,
        },
        { skipGlobalErrorToast: true }
      );
      if (res.data?.passage) patchLocalPassage(res.data.passage as PassageApiItem);
      toast.success("Décision enregistrée.");
      setRejectId(null);
      setRejectMotif("");
      await refresh();
    } catch {
      toast.error("Rejet impossible.");
    } finally {
      setBusyId(null);
    }
  };

  const exportExcel = (): void => {
    if (!filtered.length) {
      toast.warning("Aucune ligne à exporter.");
      return;
    }
    const rows = filtered.map((p) => ({
      Client: clientLabel(p),
      Passage: p.id,
      DatePrevue: p.datePrevue,
      Tournee: p.tournee?.numero ?? "",
      Equipe: p.tourneeId != null ? tourneeById.get(p.tourneeId)?.equipe?.nom ?? "" : "",
      Statut: passageStatutLecture(p).title,
      GPS: gpsBadgeText(gpsBadgeKind(p)),
      Photos: photoFraction(p).label,
      Validation: passageValidationColumnLabel(p),
      Facturable: p.isFacturable ? "Oui" : "Non",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Passages");
    XLSX.writeFile(wb, `execution-passages-${todayIso}.xlsx`);
    toast.success("Export généré.");
  };

  const renderRowActions = (p: PassageApiItem): React.ReactNode => {
    const s = effectiveStatutUpper(p);
    const busy = busyId === p.id;

    const startUi = passageExecutionStartUi(p);
    const execUp = String(p.executionStatus ?? "").toUpperCase();
    const terrainPhotos =
      execUp === "EN_COURS" ||
      Boolean(p.startedAt) ||
      s === "EN_COURS" ||
      s === "EN_ROUTE";
    const canFinish = execUp === "EN_COURS" || s === "EN_COURS";
    const awaitVal = s === "EN_ATTENTE_VALIDATION";

    const showMotif = s === "NON_EFFECTUE" || s === "ANNULE";

    return (
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={() => openDrawer(p)}
          className="rounded-lg border border-slate-600 bg-slate-800/80 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-800"
        >
          Détail
        </button>

        {startUi === "start" ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void runStart(p.id)}
            className="inline-flex items-center gap-1 rounded-lg bg-sky-600/90 px-2 py-1 text-[11px] font-medium text-white hover:bg-sky-500 disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
            Démarrer
          </button>
        ) : null}
        {startUi === "not_yet" ? (
          <button
            type="button"
            disabled
            title="Créneau futur ou non prioritaire — traiter les passages en retard et du jour d’abord."
            className="cursor-not-allowed rounded-lg border border-slate-700 bg-slate-900/80 px-2 py-1 text-[10px] font-medium text-slate-500 opacity-70"
          >
            Pas encore exécutable
          </button>
        ) : null}
        {startUi === "provision_blocked" ? (
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              disabled
              title={p.provisionBlockMessage ?? "Provision insuffisante"}
              className="cursor-not-allowed rounded-lg border border-rose-900/60 bg-rose-950/40 px-2 py-1 text-[10px] font-medium text-rose-200/90 opacity-90"
            >
              Bloqué — provision insuffisante
            </button>
            {p.clientId != null ? (
              <Link
                to={`/admin/clients/${p.clientId}?tab=provision`}
                className="text-[10px] font-medium text-sky-300 underline-offset-2 hover:underline"
              >
                Voir provision
              </Link>
            ) : null}
          </div>
        ) : null}

        {terrainPhotos ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setPreuveModal({ passageId: p.id, type: "AVANT" });
                setPreuveUrl("");
                setPreuveLat("");
                setPreuveLng("");
              }}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-800"
            >
              <Camera className="h-3 w-3" /> AVANT
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setPreuveModal({ passageId: p.id, type: "APRES" });
                setPreuveUrl("");
                setPreuveLat("");
                setPreuveLng("");
              }}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-800"
            >
              <Camera className="h-3 w-3" /> APRÈS
            </button>
          </>
        ) : null}

        {canFinish ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setFinishId(p.id);
              setFinishLat("");
              setFinishLng("");
              setFinishComment("");
            }}
            className="inline-flex items-center gap-1 rounded-lg bg-violet-600/90 px-2 py-1 text-[11px] font-medium text-white hover:bg-violet-500 disabled:opacity-40"
          >
            <Flag className="h-3 w-3" /> Terminer
          </button>
        ) : null}

        {awaitVal && canVal ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setValidateId(p.id);
                setValidateComment("");
              }}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600/90 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-500 disabled:opacity-40"
            >
              <ShieldCheck className="h-3 w-3" /> Valider
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setRejectId(p.id);
                setRejectMotif("");
                setRejectStatut("NON_EFFECTUE");
              }}
              className="inline-flex items-center gap-1 rounded-lg bg-rose-600/90 px-2 py-1 text-[11px] font-medium text-white hover:bg-rose-500 disabled:opacity-40"
            >
              <XCircle className="h-3 w-3" /> Rejeter
            </button>
          </>
        ) : null}

        {awaitVal && !canVal ? (
          <span className="text-[10px] text-slate-500">Validation réservée superviseur</span>
        ) : null}

        {(s === "EFFECTUE" || s === "PARTIEL") && (
          <button
            type="button"
            onClick={() => openDrawer(p)}
            className="inline-flex items-center gap-1 rounded-lg border border-emerald-700/50 px-2 py-1 text-[11px] text-emerald-300 hover:bg-emerald-950/40"
          >
            <CheckCircle2 className="h-3 w-3" /> Preuves
          </button>
        )}

        {showMotif ? (
          <button
            type="button"
            onClick={() => openDrawer(p)}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800"
          >
            Motif
          </button>
        ) : null}
      </div>
    );
  };

  function renderPassageTableRow(p: PassageApiItem): React.JSX.Element {
    const tour = pickTournee(p);
    const gps = gpsBadgeKind(p);
    const ph = photoFraction(p);
    const vu = validationUi(p);
    const lectureStatut = passageStatutLecture(p);
    const validationSub = validationQualiteSubtitle(p);
    const showRetardBadge = p.executionOperationalStatus === "EN_RETARD_A_EXECUTER";
    return (
      <tr key={p.id} className="border-b border-slate-800/80 hover:bg-slate-800/40">
        <td className="px-4 py-3 font-medium text-slate-100">{clientLabel(p)}</td>
        <td className="px-4 py-3 font-mono text-slate-300">#{p.id}</td>
        <td className="px-4 py-3 text-slate-300">
          {new Date(p.datePrevue).toLocaleString("fr-FR", {
            dateStyle: "short",
            timeStyle: "short",
          })}
        </td>
        <td className="px-4 py-3 text-slate-300">
          {p.tournee?.numero ?? (p.tourneeId != null ? `#${p.tourneeId}` : "—")}
        </td>
        <td className="px-4 py-3 text-slate-300">{tour?.equipe?.nom ?? "—"}</td>
        <td className="px-4 py-3">
          <div className="flex max-w-[13rem] flex-col gap-0.5">
            {showRetardBadge ? (
              <span className="inline-flex w-fit rounded-md bg-rose-600/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-100 ring-1 ring-rose-500/45">
                En retard à exécuter
              </span>
            ) : null}
            <span
              className={`inline-flex w-fit rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${passageStatutLectureBadgeClass(p)}`}
            >
              {passageExecutionOperationalColumnLabel(p)}
            </span>
            {p.executionOperationalBadgesFr?.length ? (
              <span className="text-[10px] leading-snug text-slate-400">{p.executionOperationalBadgesFr.join(" · ")}</span>
            ) : null}
            {lectureStatut.subtitle ? (
              <span className="text-[10px] leading-snug text-rose-300/90">{lectureStatut.subtitle}</span>
            ) : null}
          </div>
        </td>
        <td className="px-4 py-3">
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${gpsBadgeClasses(gps)}`}
          >
            {gpsBadgeText(gps)}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className="rounded-md bg-slate-800 px-2 py-0.5 font-mono text-[11px] text-slate-200">{ph.label}</span>
        </td>
        <td className="px-4 py-3">
          <div className="flex max-w-[12rem] flex-col gap-0.5">
            <span
              className={`inline-flex w-fit rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${validationBadgeClasses(vu)}`}
            >
              {passageValidationColumnLabel(p)}
            </span>
            {validationSub ? (
              <span className="text-[10px] leading-snug text-slate-500">{validationSub}</span>
            ) : null}
          </div>
        </td>
        <td className="px-4 py-3">
          <span className={p.isFacturable ? "font-medium text-emerald-400" : "text-slate-500"}>
            {p.isFacturable ? "Oui" : "Non"}
          </span>
        </td>
        <td className="px-4 py-3 text-right">{renderRowActions(p)}</td>
      </tr>
    );
  }

  if (loading && passages.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
        <p className="mt-3 text-sm">Chargement des passages…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <ToastContainer position="top-right" theme="dark" />
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full space-y-6 px-4 py-6 md:px-8"
      >
        <header className="flex flex-col gap-4 border-b border-slate-800 pb-6 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-white md:text-3xl">Exécution des passages</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-400">
              Suivi des passages terrain, preuves photo, GPS, validation et facturation.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"
            >
              Retour
            </button>
            <Link
              to="/admin/dashboard"
              className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"
            >
              Dashboard
            </Link>
            <Link
              to={ADMIN_PASSAGES}
              className="inline-flex items-center gap-2 rounded-xl border border-sky-700/50 bg-sky-950/40 px-4 py-2 text-sm font-medium text-sky-200 hover:bg-sky-950/70"
            >
              <MapPin className="h-4 w-4" /> Voir passages
            </Link>
            <Link
              to={ADMIN_TOURNEES}
              className="inline-flex items-center gap-2 rounded-xl border border-violet-700/50 bg-violet-950/30 px-4 py-2 text-sm font-medium text-violet-200 hover:bg-violet-950/50"
            >
              <Navigation className="h-4 w-4" /> Voir tournées
            </Link>
            <button
              type="button"
              onClick={exportExcel}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-500"
            >
              <FileSpreadsheet className="h-4 w-4" /> Exporter
            </button>
          </div>
        </header>

        <section
          className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-950/50 via-slate-900/90 to-slate-950 p-5 shadow-lg ring-1 ring-amber-500/15"
          aria-labelledby="notice-facturation"
        >
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500/20 text-amber-200">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <h2 id="notice-facturation" className="text-base font-semibold text-amber-100">
                Non validé = non facturable
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">
                Une exécution devient facturable uniquement après photo AVANT, photo APRÈS, GPS/heure serveur et
                validation superviseur. Le flag <span className="font-mono text-emerald-300/95">isFacturable</span>{" "}
                n’est activé qu’après cette chaîne complète.
              </p>
            </div>
          </div>
        </section>

        <section aria-label="Indicateurs" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <KpiTile
            label="Passages à exécuter"
            value={kpis.passagesAExecuter}
            hint={
              apiKpis
                ? `${kpis.enRetard} retard · ${kpis.aujourdhui} jour · ${apiKpis.passagesActionnables} actionnables`
                : `${kpis.enRetard} en retard · ${kpis.aujourdhui} aujourd’hui`
            }
            accent="sky"
          />
          <KpiTile label="En cours" value={kpis.enCours} accent="amber" />
          <KpiTile label="En attente validation" value={kpis.enAttenteValidation} accent="amber" />
          <KpiTile label="Validés" value={kpis.valides} accent="emerald" />
          <KpiTile label="Suspects GPS" value={kpis.suspectsGps} accent="rose" />
          <KpiTile label="Facturables" value={kpis.facturables} accent="violet" hint="isFacturable = true" />
        </section>

        <section className="space-y-3">
          <ClientDemandesAdminPanel crossLinkTo={ADMIN_PASSAGES} crossLinkLabel="Planning passages →" />
          <div className="flex flex-wrap gap-2 rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3">
            <Link
              to={ADMIN_PLAN}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-xs font-medium text-slate-200 ring-1 ring-slate-700 hover:bg-slate-800/90"
            >
              <ClipboardList className="h-4 w-4" /> Ouvrir demandes
            </Link>
            <Link
              to={ADMIN_PASSAGES}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-xs font-medium text-slate-200 ring-1 ring-slate-700 hover:bg-slate-800/90"
            >
              <ExternalLink className="h-4 w-4" /> Voir passages créés
            </Link>
            <p className="ml-auto text-xs text-slate-500">
              Les demandes sont traitées en planification — aucune intervention libre depuis cet écran.
            </p>
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h2 className="text-lg font-semibold text-white">Passages à exécuter</h2>
            <div className="flex w-full max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              <label className="flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2 text-xs text-slate-300">
                <input
                  type="checkbox"
                  checked={includeUpcoming}
                  onChange={(e) => setIncludeUpcoming(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-sky-500 focus:ring-sky-500/40"
                />
                Afficher les prochains passages
              </label>
              <label className="flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2 text-xs text-slate-300">
                <input
                  type="checkbox"
                  checked={includeBlocked}
                  onChange={(e) => setIncludeBlocked(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-sky-500 focus:ring-sky-500/40"
                />
                Afficher passages bloqués
              </label>
              <input
                type="search"
                placeholder="Rechercher client, n° passage, tournée, zone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-100 placeholder:text-slate-500 md:w-96"
              />
            </div>
          </div>

          {!filtered.length ? (
            <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 px-6 py-14 text-center">
              <p className="text-lg font-medium text-slate-300">Aucun passage à afficher pour l’instant.</p>
              <p className="mt-2 text-sm text-slate-500">
                Par défaut : en cours, retards, aujourd’hui et en attente de validation. Cochez « Afficher les prochains
                passages » pour voir les créneaux futurs (sans doublons).
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link
                  to={ADMIN_PASSAGES}
                  className="rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-500"
                >
                  Voir planning des passages
                </Link>
                <Link
                  to={ADMIN_TOURNEES}
                  className="rounded-xl border border-slate-600 px-5 py-2.5 text-sm font-medium text-slate-200 hover:bg-slate-800"
                >
                  Voir tournées
                </Link>
                <Link
                  to={ADMIN_PLAN}
                  className="rounded-xl border border-emerald-700/50 bg-emerald-950/40 px-5 py-2.5 text-sm font-medium text-emerald-200 hover:bg-emerald-950/60"
                >
                  Générer passages depuis abonnements
                </Link>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/30 shadow-inner">
              <table className="min-w-[1100px] w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-900/80 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Client</th>
                    <th className="px-4 py-3">Passage</th>
                    <th className="px-4 py-3">Date / heure prévue</th>
                    <th className="px-4 py-3">Tournée</th>
                    <th className="px-4 py-3">Équipe</th>
                    <th className="px-4 py-3">Statut</th>
                    <th className="px-4 py-3">GPS</th>
                    <th className="px-4 py-3">Photos</th>
                    <th className="px-4 py-3">Validation</th>
                    <th className="px-4 py-3">Facturable</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {executionSections.enCours.length > 0 ? (
                    <Fragment key="exec-encours">
                      <tr className="border-b border-slate-800 bg-amber-950/25">
                        <td
                          colSpan={11}
                          className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-amber-100"
                        >
                          En cours
                        </td>
                      </tr>
                      {executionSections.enCours.map((p) => renderPassageTableRow(p))}
                    </Fragment>
                  ) : null}
                  {executionSections.overdue.length > 0 ? (
                    <Fragment key="exec-overdue">
                      <tr className="border-b border-slate-800 bg-rose-950/25">
                        <td
                          colSpan={11}
                          className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-rose-100"
                        >
                          En retard à exécuter
                        </td>
                      </tr>
                      {executionSections.overdue.map((p) => renderPassageTableRow(p))}
                    </Fragment>
                  ) : null}
                  {executionSections.today.length > 0 ? (
                    <Fragment key="exec-today">
                      <tr className="border-b border-slate-800 bg-sky-950/20">
                        <td
                          colSpan={11}
                          className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-sky-100"
                        >
                          Aujourd’hui
                        </td>
                      </tr>
                      {executionSections.today.map((p) => renderPassageTableRow(p))}
                    </Fragment>
                  ) : null}
                  {executionSections.enAttenteValidation.length > 0 ? (
                    <Fragment key="exec-validation">
                      <tr className="border-b border-slate-800 bg-violet-950/20">
                        <td
                          colSpan={11}
                          className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-violet-100"
                        >
                          En attente validation
                        </td>
                      </tr>
                      {executionSections.enAttenteValidation.map((p) => renderPassageTableRow(p))}
                    </Fragment>
                  ) : null}
                  {includeUpcoming && executionSections.upcoming.length > 0 ? (
                    <Fragment key="exec-upcoming">
                      <tr className="border-b border-slate-800 bg-slate-900/80">
                        <td
                          colSpan={11}
                          className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-400"
                        >
                          Prochains passages
                        </td>
                      </tr>
                      {executionSections.upcoming.map((p) => renderPassageTableRow(p))}
                    </Fragment>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <footer className="border-t border-slate-800 pt-6 text-center text-xs text-slate-600">
          EspoirAfriqueSas — exécution terrain alignée passage (pas d’intervention libre).
        </footer>
      </motion.div>

      <PassageExecutionDrawer
        open={drawerPassage != null}
        passage={drawerPassage}
        tournee={drawerPassage ? pickTournee(drawerPassage) : null}
        onClose={() => setDrawerPassage(null)}
      />

      {preuveModal ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-white">Preuve photo {preuveModal.type}</h3>
            <p className="mt-2 text-xs text-slate-500">
              Collez l’URL publique après dépôt du fichier (ex. /uploads/…). GPS optionnel.
            </p>
            <label className="mt-4 block text-xs font-medium text-slate-400">
              URL photo
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                value={preuveUrl}
                onChange={(e) => setPreuveUrl(e.target.value)}
                placeholder="https://… ou /uploads/…"
              />
            </label>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <label className="text-xs text-slate-400">
                Lat.
                <input
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
                  value={preuveLat}
                  onChange={(e) => setPreuveLat(e.target.value)}
                />
              </label>
              <label className="text-xs text-slate-400">
                Long.
                <input
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
                  value={preuveLng}
                  onChange={(e) => setPreuveLng(e.target.value)}
                />
              </label>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-600 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
                onClick={() => void coordsFromBrowser().then((c) => {
                  if (!c) {
                    toast.info("GPS indisponible.");
                    return;
                  }
                  setPreuveLat(String(c.latitude));
                  setPreuveLng(String(c.longitude));
                })}
              >
                Remplir GPS navigateur
              </button>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm text-slate-400 hover:text-white"
                onClick={() => setPreuveModal(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={busyId === preuveModal.passageId}
                className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-40"
                onClick={() => void submitPreuve()}
              >
                Envoyer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {finishId != null ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-white">Terminer le passage</h3>
            <p className="mt-2 text-xs text-slate-500">GPS fin recommandé — aligné POST /passages/:id/finish</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <label className="text-xs text-slate-400">
                Lat.
                <input
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
                  value={finishLat}
                  onChange={(e) => setFinishLat(e.target.value)}
                />
              </label>
              <label className="text-xs text-slate-400">
                Long.
                <input
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
                  value={finishLng}
                  onChange={(e) => setFinishLng(e.target.value)}
                />
              </label>
            </div>
            <label className="mt-3 block text-xs text-slate-400">
              Commentaire
              <textarea
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                rows={2}
                value={finishComment}
                onChange={(e) => setFinishComment(e.target.value)}
              />
            </label>
            <div className="mt-3">
              <button
                type="button"
                className="text-xs text-sky-400 hover:underline"
                onClick={() => void coordsFromBrowser().then((c) => {
                  if (!c) {
                    toast.info("GPS indisponible.");
                    return;
                  }
                  setFinishLat(String(c.latitude));
                  setFinishLng(String(c.longitude));
                })}
              >
                Utiliser ma position
              </button>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm text-slate-400" onClick={() => setFinishId(null)}>
                Annuler
              </button>
              <button
                type="button"
                disabled={busyId === finishId}
                className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-40"
                onClick={() => void runFinish()}
              >
                Terminer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {validateId != null ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-white">Valider l’exécution</h3>
            <textarea
              className="mt-4 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              placeholder="Commentaire validation (optionnel)"
              rows={3}
              value={validateComment}
              onChange={(e) => setValidateComment(e.target.value)}
            />
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm text-slate-400" onClick={() => setValidateId(null)}>
                Annuler
              </button>
              <button
                type="button"
                disabled={busyId === validateId}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-40"
                onClick={() => void runValidate()}
              >
                Confirmer validation
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {rejectId != null ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-white">Rejeter ou reporter</h3>
            <textarea
              className="mt-4 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              placeholder="Motif (min. 5 caractères)"
              rows={3}
              value={rejectMotif}
              onChange={(e) => setRejectMotif(e.target.value)}
            />
            <label className="mt-3 block text-xs text-slate-400">
              Statut cible
              <select
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                value={rejectStatut}
                onChange={(e) => setRejectStatut(e.target.value as "NON_EFFECTUE" | "REPORTE")}
              >
                <option value="NON_EFFECTUE">NON_EFFECTUE</option>
                <option value="REPORTE">REPORTE</option>
              </select>
            </label>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm text-slate-400" onClick={() => setRejectId(null)}>
                Annuler
              </button>
              <button
                type="button"
                disabled={busyId === rejectId}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-500 disabled:opacity-40"
                onClick={() => void runReject()}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default InterventionsPage;
